import { useEffect } from 'react';
import { useRecoilValue } from 'recoil';
import { LineOptions, selectedToolState } from '~/state/atoms/toolsAtoms';
import { isTextEntryElement } from '~/utils/textEntry';
import { useSwitchLineMode } from './useSwitchLineMode';

const TOGGLE_KEY = 'e';

/**
 * Pure logic: return the line mode after one erase toggle. A `null` mode is
 * treated as not-erasing (matching the toolbar toggle, whose off state writes
 * `'draw'`), so it flips to `'erase'`.
 */
export function nextLineMode(mode: LineOptions['mode']): 'draw' | 'erase' {
    return mode === 'erase' ? 'draw' : 'erase';
}

/**
 * Hook: listen for the plain E key and flip the Line tool between draw and
 * erase mode through {@link useSwitchLineMode}, as the toolbar toggle does:
 * while a line is in progress the mode stays and a warning says why.
 *
 * Guards (early-return so other handlers still process the event):
 *  - Any modifier (Ctrl/Cmd/Alt/Shift) held — preserves OS/browser shortcuts.
 *  - The event is an auto-repeat (avoids rapid toggles when the key is held).
 *  - The active element is an INPUT, TEXTAREA, or contentEditable element.
 *  - The Line tool is not the selected tool.
 */
export function useLineEraseToggleShortcut(): void {
    const selectedTool = useRecoilValue(selectedToolState);
    const switchLineMode = useSwitchLineMode();

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent): void => {
            if (
                event.ctrlKey ||
                event.metaKey ||
                event.altKey ||
                event.shiftKey
            ) {
                return;
            }
            if (event.repeat) {
                return;
            }
            if (event.key.toLowerCase() !== TOGGLE_KEY) {
                return;
            }
            if (selectedTool !== 'line') {
                return;
            }
            if (isTextEntryElement(document.activeElement)) {
                return;
            }
            switchLineMode(nextLineMode);
        };

        window.addEventListener('keydown', onKeyDown);
        return () => {
            window.removeEventListener('keydown', onKeyDown);
        };
    }, [selectedTool, switchLineMode]);
}
